import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { type Paginate } from "@/types";

import { type Category } from "@/modules/news/user/types";

export type CategoryList = Paginate<Category>;

export const categories = createQueryKeys("news-categories", {
  all: null,
  one: (id?: number) => [id],
});

const include = "";

export const fetchCategoriesQueryOptions = () =>
  queryOptions({
    ...categories.all,
    queryFn: () =>
      api
        .get<CategoryList>(`/news/categories`, {
          params: { include },
        })
        .then((response) => response.data),
  });

export const useFetchCategories = () => useQuery(fetchCategoriesQueryOptions());

export const fetchCategoryQueryOptions = (id?: number) =>
  queryOptions({
    ...categories.one(id),
    queryFn: () =>
      api
        .get<Category>(`/news/categories/${id}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchCategory = (id?: number) =>
  useQuery(fetchCategoryQueryOptions(id));

export const useFollowCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .put(`/news/categories/${id}/follow`)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(categories.all);
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};

export const useUnfollowCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api
        .delete(`/news/categories/${id}/follow`)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(categories.all);
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};
